import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useState } from 'react';

import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Input } from '../../../web/components-v2/Input';
import { THEME } from '../../../web/components-v2/Theme/theme';
import type { FollowUp } from './types';

type Importance = 'low' | 'medium' | 'high';

export type CustomFollowUpModalProps = {
	/** Controlled open state. */
	open: boolean;
	/** Fired when the dialog requests open/close (Set, Cancel, X, or backdrop). */
	onOpenChange: (open: boolean) => void;
	/** Optional initial state, used when re-opening to edit an existing scheduled follow-up. */
	initial?: { name: string; date: string; importance: Importance };
	/** Fired with the resulting `FollowUp.scheduled` payload when the user clicks Set. */
	onConfirm: (followUp: Extract<FollowUp, { kind: 'scheduled' }>) => void;
};

const IMPORTANCE_OPTIONS: ReadonlyArray<{ value: Importance; label: string; color: string }> = [
	{ value: 'low', label: 'Low importance', color: THEME.colors.chart.tricksterGreen },
	{ value: 'medium', label: 'Medium importance', color: THEME.colors.chart.magicOrange },
	{ value: 'high', label: 'High importance', color: THEME.colors.chart.spellRed },
];

const DEFAULT_TIME = '09:00';

/**
 * Splits an ISO date-time into the `YYYY-MM-DD` and `HH:mm` parts the modal's
 * inputs expect. Falls back to empty / default values if the ISO is missing or
 * unparseable.
 */
const splitInitialDateTime = (iso?: string): { date: string; time: string } => {
	if (!iso) return { date: '', time: DEFAULT_TIME };
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) return { date: '', time: DEFAULT_TIME };
	const yyyy = parsed.getFullYear().toString().padStart(4, '0');
	const mm = (parsed.getMonth() + 1).toString().padStart(2, '0');
	const dd = parsed.getDate().toString().padStart(2, '0');
	const hh = parsed.getHours().toString().padStart(2, '0');
	const mi = parsed.getMinutes().toString().padStart(2, '0');
	return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
};

/**
 * Custom follow-up modal for the focused-view wrap-up. Lets the rep enter a
 * Name + Date + Time + Spice Level (importance) and emits a
 * `FollowUp.scheduled` payload via `onConfirm`.
 *
 * Built fresh on components-v2 primitives — the legacy queue's
 * `aida/components/followup/FollowUpModal` is MobX-coupled (observer + queue
 * VM) and is intentionally not imported here. The 3 spice buttons are
 * deliberately raw `<button>`s: there's no components-v2 toggle-group for an
 * icon-only 3-way picker, so we build a small inline one.
 */
export const CustomFollowUpModal: FC<CustomFollowUpModalProps> = ({ open, onOpenChange, initial, onConfirm }) => {
	const [name, setName] = useState(initial?.name ?? '');
	const initialSplit = splitInitialDateTime(initial?.date);
	const [date, setDate] = useState<string>(initialSplit.date);
	const [time, setTime] = useState<string>(initialSplit.time);
	const [importance, setImportance] = useState<Importance>(initial?.importance ?? 'medium');

	// When the dialog re-opens, reset internal state to the (possibly new)
	// initial values so a stale entry from a previous open doesn't leak.
	useEffect(() => {
		if (open) {
			const { date: d, time: t } = splitInitialDateTime(initial?.date);
			setName(initial?.name ?? '');
			setDate(d);
			setTime(t);
			setImportance(initial?.importance ?? 'medium');
		}
	}, [open, initial?.name, initial?.date, initial?.importance]);

	const trimmedName = name.trim();
	const hasRequiredFields = trimmedName.length > 0 && date.length > 0 && time.length > 0;

	// Compute the picked date+time as a single Date so we can validate it's in
	// the future. `new Date('YYYY-MM-DDTHH:mm:00')` parses as the user's local
	// time (matches what they typed). Mirrors the legacy
	// `aida/components/followup/FollowUpModal:67-72` "must be in the future"
	// guard, but inline (no errorMessages bus in focused-view).
	const selectedAt = hasRequiredFields ? new Date(`${date}T${time}:00`) : null;
	const isValidDate = selectedAt !== null && !Number.isNaN(selectedAt.getTime());
	const isPast = isValidDate && (selectedAt as Date).getTime() < Date.now();
	const canSet = hasRequiredFields && isValidDate && !isPast;

	const handleSet = () => {
		if (!canSet || !selectedAt) return;
		// Converting to ISO normalizes to UTC for storage.
		const isoDate = selectedAt.toISOString();
		onConfirm({ kind: 'scheduled', date: isoDate, importance, name: trimmedName });
		onOpenChange(false);
	};

	const handleCancel = () => onOpenChange(false);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<DialogTitle>Follow up</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styles.fields)}>
						<div className={css(styles.field)}>
							<label className={css(styles.label)} htmlFor='custom-followup-name'>
								Name
							</label>
							<Input
								id='custom-followup-name'
								type='text'
								value={name}
								onChange={e => setName(e.target.value)}
								placeholder='Describe your follow up'
							/>
						</div>

						<div className={css(styles.field)}>
							<label className={css(styles.label)}>Date &amp; Time</label>
							<div className={css(styles.dateTimeRow)}>
								<div className={css(styles.dateWrap)}>
									<DateInput value={date} onChange={setDate} />
								</div>
								<Input
									aria-label='Time'
									type='time'
									value={time}
									onChange={e => setTime(e.target.value)}
									styles={[styles.timeInput]}
								/>
							</div>
							{isPast && (
								<div className={css(styles.errorText)} role='alert'>
									Follow-up must be in the future.
								</div>
							)}
						</div>

						<div className={css(styles.field)}>
							<label className={css(styles.label)}>Spice Level</label>
							<div className={css(styles.spiceRow)} role='radiogroup' aria-label='Spice Level'>
								{IMPORTANCE_OPTIONS.map(opt => {
									const selected = importance === opt.value;
									return (
										<button
											key={opt.value}
											type='button'
											role='radio'
											aria-checked={selected}
											aria-pressed={selected}
											aria-label={opt.label}
											onClick={() => setImportance(opt.value)}
											className={css(styles.spiceButton, selected && styles.spiceButtonSelected)}
											style={selected ? { borderColor: opt.color, background: `${opt.color}22` } : undefined}
										>
											<ChiliIcon color={opt.color} />
										</button>
									);
								})}
							</div>
						</div>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={handleCancel}>
						Cancel
					</Button>
					<Button onClick={handleSet} disabled={!canSet}>
						Set
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

/**
 * Tiny inline chili-pepper icon used for the spice picker. Kept inline so we
 * don't have to add another file under `web/components/svgs/icons/` for a
 * one-off graphic; the 3 colors are passed in by the parent.
 */
const ChiliIcon: FC<{ color: string }> = ({ color }) => (
	<svg width='20' height='20' viewBox='0 0 24 24' fill='none' aria-hidden='true' xmlns='http://www.w3.org/2000/svg'>
		<path d='M5 18.5C5 13 9 9.5 13 9.5C16.5 9.5 19 12 19 14.5C19 17.5 16.5 20 13 20C9 20 5 19.5 5 18.5Z' fill={color} />
		<path
			d='M13 9.5C13 7.5 14 6 15.5 5.5C16.5 5.2 17.5 5 18 4.5'
			stroke='#3B7A1E'
			strokeWidth='1.5'
			strokeLinecap='round'
			fill='none'
		/>
	</svg>
);

const styles = StyleSheet.create({
	popup: {
		maxWidth: 440,
	},
	fields: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	field: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	label: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	dateTimeRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	dateWrap: {
		flex: 1,
		minWidth: 0,
	},
	timeInput: {
		width: 120,
		flexShrink: 0,
	},
	errorText: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.chart.spellRed,
		marginTop: THEME.spacing.xs,
	},
	spiceRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	spiceButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 40,
		height: 40,
		padding: 0,
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.main}`,
		background: THEME.colors.background.light,
		cursor: 'pointer',
		transitionProperty: 'border-color, background-color',
		transitionDuration: '120ms',
		transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		':hover': {
			borderColor: THEME.colors.border.light,
		},
	},
	spiceButtonSelected: {
		// Border color + tint are applied inline via the active option's color
		// so the visual cue follows the chili palette (green/orange/red).
		borderWidth: 2,
	},
});
